import React, { useState, useEffect, useMemo, useRef } from "react";
import { Columns3, Paperclip } from "lucide-react";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import GlobalDataTable from "../../../global/GlobalDataTable";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";
import "../../../shared/styles/VolunteerTable.css";

const COLUMN_VISIBILITY_KEY = "girinestham.inwardOutwardList.columnVisibility";

const CREATED_BY_COLUMN = {
  id: "created_by_name",
  label: "Created By",
  defaultVisible: true,
};

export const getInwardOutwardColumnDefs = (registerType, { includeCreatedBy = false } = {}) => {
  if (registerType === "inward") {
    const cols = [
      { id: "inward_no", label: "Reference No", defaultVisible: true },
      { id: "date_received", label: "Date Received", defaultVisible: true },
      { id: "from_name", label: "From Name", defaultVisible: true },
      { id: "from_address", label: "From Address", defaultVisible: false },
      { id: "document_type_name", label: "Document Type", defaultVisible: true },
      { id: "subject", label: "Subject", defaultVisible: true },
      { id: "dispatch_mode_name", label: "Dispatch Mode", defaultVisible: false },
      { id: "received_by_name", label: "Received By", defaultVisible: true },
      { id: "remarks", label: "Remarks", defaultVisible: false },
      { id: "attachment", label: "Attachment", defaultVisible: true },
    ];
    if (includeCreatedBy) {
      cols.splice(cols.length - 1, 0, { ...CREATED_BY_COLUMN });
    }
    return cols;
  }

  const cols = [
    { id: "outward_no", label: "Reference No", defaultVisible: true },
    { id: "date_sent", label: "Date Sent", defaultVisible: true },
    { id: "to_name", label: "To Name", defaultVisible: true },
    { id: "to_address", label: "To Address", defaultVisible: false },
    { id: "document_type_name", label: "Document Type", defaultVisible: true },
    { id: "subject", label: "Subject", defaultVisible: true },
    { id: "dispatch_mode_name", label: "Dispatch Mode", defaultVisible: false },
    { id: "dispatched_by_name", label: "Dispatched By", defaultVisible: true },
    { id: "acknowledgement_no", label: "Acknowledgement No", defaultVisible: false },
    { id: "remarks", label: "Remarks", defaultVisible: false },
    { id: "attachment", label: "Attachment", defaultVisible: true },
  ];
  if (includeCreatedBy) {
    cols.splice(cols.length - 1, 0, { ...CREATED_BY_COLUMN });
  }
  return cols;
};

export function formatInwardOutwardCreatedByName(row) {
  const hasCreator =
    row?.created_by != null && row?.created_by !== "";
  if (!hasCreator) return "null";
  const name = String(row?.created_by_name ?? "").trim();
  return name || "null";
}

export function loadInwardOutwardSavedVisibility(registerType) {
  try {
    const raw = localStorage.getItem(`${COLUMN_VISIBILITY_KEY}.${registerType}`);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function getInwardOutwardCellText(columnId, m) {
  switch (columnId) {
    case "date_received":
    case "date_sent": {
      if (!m[columnId]) return "—";
      return new Date(m[columnId]).toLocaleDateString("en-GB");
    }
    case "received_by_name":
    case "dispatched_by_name":
      return m.name || m.received_by_name || m.dispatched_by_name || "—";
    case "created_by_name":
      return formatInwardOutwardCreatedByName(m);
    case "attachment":
      return m.attachment ? "Attached" : "—";
    default:
      return m[columnId] ?? "—";
  }
}

function cellValue(columnId, m) {
  switch (columnId) {
    case "date_received":
    case "date_sent": {
      if (!m[columnId]) return "—";
      return new Date(m[columnId]).toLocaleDateString("en-GB");
    }
    case "received_by_name":
    case "dispatched_by_name":
      return m.name || m.received_by_name || m.dispatched_by_name || "—";
    case "created_by_name":
      return formatInwardOutwardCreatedByName(m);
    case "attachment":
      if (m.attachment) {
        const fileUrl = resolveMediaUrl(m.attachment);
        return (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="View Attachment"
            style={{ color: "var(--ub-teal-dark)", display: "inline-flex", alignItems: "center" }}
          >
            <Paperclip size={16} />
          </a>
        );
      }
      return "—";
    default:
      return m[columnId] ?? "—";
  }
}

function downloadCsv(filename, headers, rows) {
  const escapeCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [
    headers.map(escapeCell).join(","),
    ...rows.map((r) => r.map(escapeCell).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const RegisterListTable = ({
  records,
  registerType,
  onDelete,
  onRequestEdit,
  loading,
  showExport = true,
  showToolbar = true,
  includeCreatedBy = false,
  columnVisibilityOverride = null,
  onColumnVisibilityChange = null,
}) => {
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const columnDefs = useMemo(
    () => getInwardOutwardColumnDefs(registerType, { includeCreatedBy }),
    [registerType, includeCreatedBy],
  );

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadInwardOutwardSavedVisibility(registerType);
    const initial = {};
    for (const c of columnDefs) {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    }
    return initial;
  });

  useEffect(() => {
    const saved = loadInwardOutwardSavedVisibility(registerType);
    const initial = {};
    for (const c of columnDefs) {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    }
    setColumnVisibility(initial);
  }, [registerType, columnDefs]);

  useEffect(() => {
    if (!onColumnVisibilityChange) return;
    onColumnVisibilityChange(columnVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnVisibility]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [columnPickerOpen]);

  const effectiveVisibility = columnVisibilityOverride || columnVisibility;

  const visibleDataColumns = useMemo(
    () => columnDefs.filter((c) => effectiveVisibility[c.id]),
    [effectiveVisibility, columnDefs],
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions",
      },
    ],
    [visibleDataColumns],
  );

  const toggleColumn = (id) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = columnDefs.filter((c) => next[c.id]).length;
      if (visibleCount === 0) return prev;
      try {
        localStorage.setItem(`${COLUMN_VISIBILITY_KEY}.${registerType}`, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const exportVisibleToCsv = () => {
    const headers = visibleDataColumns.map((c) => c.label);
    const rows = records.map((m) =>
      visibleDataColumns.map((c) => cellValue(c.id, m)),
    );
    downloadCsv(`${registerType}_register.csv`, headers, rows);
  };

  if (loading) return <p className="ub-loading">Loading records…</p>;

  return (
    <div className="ub-table-section">
      {showToolbar ? (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: "0.5rem",
            marginBottom: "0.75rem",
          }}
        >
          <div className="vt-column-picker" ref={pickerRef} style={{ marginLeft: 0 }}>
            <GlobalButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setColumnPickerOpen((o) => !o)}
              aria-expanded={columnPickerOpen}
              aria-controls="inward-outward-list-column-panel"
              style={{ whiteSpace: "nowrap" }}
            >
              <Columns3 size={16} aria-hidden />
              <span style={{ marginLeft: "0.35rem" }}>Columns</span>
            </GlobalButton>
            {columnPickerOpen && (
              <div
                id="inward-outward-list-column-panel"
                className="vt-column-picker__panel"
                role="group"
                aria-label="Choose visible columns"
              >
                <p className="vt-column-picker__title">Show columns</p>
                <ul className="vt-column-picker__list">
                  {columnDefs.map((c) => (
                    <li key={c.id}>
                      <label className="vt-column-picker__row">
                        <input
                          type="checkbox"
                          checked={!!columnVisibility[c.id]}
                          onChange={() => toggleColumn(c.id)}
                        />
                        <span>{c.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {showExport && (
            <GlobalButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={exportVisibleToCsv}
              style={{ whiteSpace: "nowrap" }}
            >
              Export CSV
            </GlobalButton>
          )}
        </div>
      ) : null}

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel={`${registerType} list table`}
        headers={tableHeaders}
      >
        {records.length === 0 ? (
          <tr>
            <td
              colSpan={Math.max(visibleDataColumns.length + 1, 1)}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No {registerType} records found.
            </td>
          </tr>
        ) : (
          records.map((m) => (
            <tr key={m.id}>
              {visibleDataColumns.map((col) => (
                <td key={col.id}>
                  {col.id === "inward_no" || col.id === "outward_no" ? (
                    <span style={{ fontWeight: 500 }}>
                      {cellValue(col.id, m)}
                    </span>
                  ) : (
                    cellValue(col.id, m)
                  )}
                </td>
              ))}
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  {onRequestEdit ? (
                    <GlobalTableActionButton
                      action="edit"
                      title="Edit record in form"
                      onClick={() => onRequestEdit(m)}
                    />
                  ) : null}

                  {onDelete ? (
                    <GlobalTableActionButton
                      action="delete"
                      onClick={() => onDelete(m.id)}
                    />
                  ) : null}
                </div>
              </td>
            </tr>
          ))
        )}
      </GlobalDataTable>
    </div>
  );
};

export default RegisterListTable;
